import { Injectable } from '@angular/core';
import { logFeature } from '@office-start/telemetry-service';
import { DataSecurityLevel as otelDataSecurityLevel, KnownFeatureNames } from '@office-start/telemetry-types';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { Performance } from '../../shared/services/performance.service';
import { ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { AppServiceCommand, AppServiceMethod } from '../models/app-service-command.model';
import { AppServiceResponse, AppServiceResponseStatus } from '../models/app-service-response.model';
import { AppServiceConnection } from './app-service-connection.service';
import { PwaConfigService } from './pwa-config.service';

/**
 * App Service Connection for Office UWP.
 */
@Injectable()
export class UwpAppServiceConnection implements AppServiceConnection {

    private connectionPromise: Promise<Windows.ApplicationModel.AppService.AppServiceConnection>;
    private readonly bridgeTimeout: number;
    private readonly responseStatusMap: {[status: number]: AppServiceResponseStatus } = {};

    constructor(
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly instrumentationService: InstrumentationService,
        pwaConfigService: PwaConfigService
    ) {
        this.bridgeTimeout = pwaConfigService.config.bridgeTimeoutInSeconds * 1000;
        this.responseStatusMap[Windows.ApplicationModel.AppService.AppServiceResponseStatus.success] = AppServiceResponseStatus.Success;
        this.responseStatusMap[Windows.ApplicationModel.AppService.AppServiceResponseStatus.failure] = AppServiceResponseStatus.Failure;
        this.responseStatusMap[Windows.ApplicationModel.AppService.AppServiceResponseStatus.resourceLimitsExceeded] = AppServiceResponseStatus.ResourceLimitsExceeded;
    }

    public sendCommands(commands: Array<AppServiceCommand>): Promise<AppServiceResponse> {
        return this.validateConnection().then(connection => this.sendConnectionMessage(connection, commands));
    }

    private validateConnection(): Promise<Windows.ApplicationModel.AppService.AppServiceConnection> {
        const AppService = Windows.ApplicationModel.AppService;

        if (!this.connectionPromise) {
            this.connectionPromise = new Promise((resolve, reject) => {
                const connectStart = Performance.now();
                const connection = new AppService.AppServiceConnection();
                connection.appServiceName = 'com.microsoft.myoffice.client.bridge';
                connection.packageFamilyName = Windows.ApplicationModel.Package.current.id.familyName;
                connection.openAsync().then(connectionStatus => {
                    if (connectionStatus === AppService.AppServiceConnectionStatus.success) {
                        connection.onserviceclosed = () => {
                            this.connectionPromise = null;
                        };

                        // After initial connection, pass the correlationId and sessionId so
                        // the localbridge will use them for telemetry
                        this.sendTelemetryContext(connection, connectStart).then(() => {
                            resolve(connection);
                        });
                    } else {
                        const params = {};
                        const connectLatency = Math.ceil(Performance.now() - connectStart);
                        params[InstrumentationParam.Latency] = connectLatency.toString();
                        const errorMessage = 'Connection status: ' + connectionStatus;
                        params[InstrumentationParam.State] = 'Failed';
                        params[InstrumentationParam.Error] = errorMessage;
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.AppServiceConnection, params, [], DataSecurityLevel.Enhanced);
                        logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.AppServiceConnection, params}, otelDataSecurityLevel.Enhanced);

                        this.connectionPromise = null;
                        // Get all enum values from executing 'JSON.stringify(AppService.AppServiceConnectionStatus)' in console
                        reject(errorMessage);
                    }
                });
            });
        }

        return this.connectionPromise;
    }

    private sendTelemetryContext(connection: Windows.ApplicationModel.AppService.AppServiceConnection, connectStart: number): Promise<void> {
        const clientStartupContext = this.clientStartupConfigService.config.sharedClientStartupContext;

        return new Promise( resolve => {
            const commands: Array<AppServiceCommand> = [
                { method: AppServiceMethod.Cid, data: clientStartupContext.correlationId },
                { method: AppServiceMethod.Sid, data: clientStartupContext.sessionId }
            ];

            // We don't want this to cause the connection to fail, it just needs to send this telemetry context message
            // before returning the connection. Otherwise another caller could send a message before the context has been set
            this.sendConnectionMessage(connection, commands).then(response => {
                if (response.status !== AppServiceResponseStatus.Success) {
                    throw new Error('Response failed: ' + response.status);
                }
                return null;
            }).catch(reason => reason || 'Error').then(error => {
                const params: InstrumentationParams = {};
                const connectLatency = Math.ceil(Performance.now() - connectStart);
                params[InstrumentationParam.Latency] = connectLatency.toString();
                params[InstrumentationParam.State] = error ? 'FailedContext' : 'Success';
                if (error) {
                    params[InstrumentationParam.Error] = 'SendTelemetryContext failed: ' + error;
                }
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.AppServiceConnection, params, [], DataSecurityLevel.Enhanced);
                resolve();
            });
        });
    }

    private sendConnectionMessage(connection: Windows.ApplicationModel.AppService.AppServiceConnection, commands: Array<AppServiceCommand>): Promise<AppServiceResponse> {
        return new Promise<AppServiceResponse>((resolve, reject) => {
            const request = new Windows.Foundation.Collections.ValueSet();
            const message = {};
            commands.forEach(command => {
                message[command.method] = command.data;
            });
            request.insert('message', JSON.stringify(message));
            const messagePromise = connection.sendMessageAsync(request).then( response => {
                // TODO: We see some parsing error in logs, logic below ensures the response strings are logged so they can be investigated
                let appServiceMessage;
                try {
                    appServiceMessage = JSON.parse((response.message as any).message);
                } catch (e) {
                    reject(`JSON parsing failed. Has message: ${!response.message}, error: ${(response as any).error || ''}`);
                    return;
                }
                resolve({
                    message: appServiceMessage,
                    status: this.responseStatusMap.hasOwnProperty(response.status) ? this.responseStatusMap[response.status] : AppServiceResponseStatus.Unknown
                });
            }, reject);
            // When LocalBridge crashes, sendMessageAsync can hang until the next time it's created. We enforce a timeout to avoid waiting.
            if (this.bridgeTimeout) {
                const timer = setTimeout(() => {
                    reject('Connection timeout');
                    messagePromise.cancel();
                }, this.bridgeTimeout);
                messagePromise.then(() => clearTimeout(timer));
            }
        });
    }
}
